<template>
  <td
    v-if="visible"
    :colspan="span.colSpan"
    :rowspan="span.rowSpan"
    class="grid-cell"
    :class="cellClass"
    :style="cellStyle"
    @dblclick.stop="handleDoubleClick"
    @click.stop="handleClick"
    @mousedown="handleMouseDown"
    @mouseenter="handleMouseEnter"
    @mouseup="handleMouseUp"
    @resize="handleResize"
  >
    <slot :cell="cell" :editing="isEditing">
      <grid-editor
        v-if="cell.editor && isEditing"
        :type="cell.editor.type"
        :mode="cell.editor.mode"
        :parent-cell="cell.editor.parentCell"
        :value="value"
        :parent-value="cell.editor.parentValue"
        :options="cell.editor.options"
        :disabled-items="cell.editor.disabledItems"
        :hidden-items="cell.editor.hiddenItems"
        @change="handleChangeValue"
        style="display: inline-flex;"
      >
      </grid-editor>
      <span class="cell-text" v-else :title="cell.text">{{ cell.text }}</span>
    </slot>
  </td>
</template>
<script>
import GridEditor from "@/components/grid/Editor";
import { CellPosition } from "@/components/excel/schema/BaseSchema.ts";
import { parseValue } from "@/store/modules/table/getters.ts";
export default {
  name: "grid-cell",
  components: { GridEditor },
  props: {
    column: Object,
    row: {
      type: Object,
    },
    editingPosition: {
      type: Object,
    },
    parentCell: Function,
    selectedRangeMethod: Function,
  },
  data() {
    return {
      editorValue: {},
      flushPeriod: 1000,
      timer: null,
      timestamp: null,
    };
  },
  computed: {
    cellStyle() {
      const { style } = this.cell;
      if (!style) {
        return null;
      }
      const { front, align, border, color } = style;
      const result = {};
      if (front) {
        const { bold } = front;
        if (bold) {
          result["font-weight"] = "bold";
        }
      }
      if (align) {
        const { horizontal, vertical } = align;
        if (horizontal) {
          result["text-align"] = horizontal;
        }
        if (vertical) {
          result["vertical-align"] = vertical;
        }
      }
      if (border) {
      }
      if (color) {
        const { front, background } = color;
        if (front) {
          result["color"] = front;
        }
        if (background) {
          result["background-color"] = background;
        }
      }
      return result;
    },
    cell() {
      return this.row.cells[this.column.property] || {};
    },
    value() {
      if (this.editorValue) {
        return this.editorValue.value;
      }
      return null;
    },
    visible() {
      const { span } = this;
      if (!span) {
        return true;
      }
      return span.colSpan && span.rowSpan;
    },
    span() {
      const { cell } = this;
      if (cell && cell.span) {
        return cell.span;
      }
      return { rowSpan: 1, colSpan: 1 };
    },
    rowIndex() {
      return this.row.index;
    },
    columnIndex() {
      return this.column.index;
    },
    params() {
      const { rowIndex, columnIndex, row, column, cell } = this;
      return { rowIndex, columnIndex, row, column, cell };
    },
    position() {
      const { rowIndex, columnIndex } = this;
      return new CellPosition(rowIndex, columnIndex);
    },
    isLeftBorder() {
      const { isRangeSelected, selectedRange, position } = this;
      if (!isRangeSelected) {
        return false;
      }
      if (!selectedRange) {
        return false;
      }
      return selectedRange.inLeftBorder(position);
    },
    isRightBorder() {
      const { isRangeSelected, selectedRange, position } = this;
      if (!isRangeSelected) {
        return false;
      }
      if (!selectedRange) {
        return false;
      }
      return selectedRange.inRightBorder(position);
    },
    isTopBorder() {
      const { isRangeSelected, selectedRange, position } = this;
      if (!isRangeSelected) {
        return false;
      }
      if (!selectedRange) {
        return false;
      }
      return selectedRange.inTopBorder(position);
    },
    isBottomBorder() {
      const { isRangeSelected, selectedRange, position } = this;
      if (!isRangeSelected) {
        return false;
      }
      if (!selectedRange) {
        return false;
      }
      return selectedRange.inBottomBorder(position);
    },
    isSingleSelected() {
      const { row, column, selectedRange, position } = this;
      if (row.selected || column.selected) {
        return false;
      }
      if (!selectedRange) {
        return false;
      }
      const { start, end } = selectedRange;
      return position.isSameCell(start) && position.isSameCell(end);
    },
    isRangeSelected() {
      const { row, column, selectedRange, position } = this;
      if (row.selected || column.selected) {
        return true;
      }
      if (!selectedRange) {
        return false;
      }
      const { start, end } = selectedRange;
      return (
        !start.isSameCell(end) &&
        selectedRange.containCell(position, {
          allowSameStart: true,
          allowSameEnd: true,
        })
      );
    },
    isEditing() {
      const { editingPosition, column, row } = this;
      const { columnIndex, rowIndex } = editingPosition;
      return columnIndex === column.index && rowIndex === row.index;
    },
    selectedRange() {
      const { selectedRangeMethod, row, column } = this;
      if (selectedRangeMethod) {
        return selectedRangeMethod({ row, column });
      }
      return undefined;
    },
    cellClass() {
      const { isLeftBorder, isRightBorder, isTopBorder, isBottomBorder } = this;
      const { isSingleSelected, isRangeSelected, isEditing } = this;
      const obj = {
        selected: isSingleSelected,
        "selected-range": isRangeSelected,
        "border-left": isLeftBorder,
        "border-right": isRightBorder,
        "border-top": isTopBorder,
        "border-bottom": isBottomBorder,
        "is-editing": isEditing,
      };
      return [obj];
    },
  },
  watch: {
    isEditing(value, oldValue) {
      console.log(value, oldValue);
      if (!value && oldValue) {
        // 结束编辑状态
        this.emitChange();
        this.cleanTimerIfPresent();
      } else if (value && !oldValue) {
        // 进入编辑状态
        const { mode, value } = this.cell.editor;
        // const cellValue = parseValue(mode, value);
        console.log("我要进入编辑状态", value);
        if (this.editorValue) {
          this.editorValue.value = value;
          this.editorValue.text = this.cell.text;
        } else {
          this.editorValue = {
            value: value,
            text: this.cell.text,
          };
        }
        this.createTimer();
      }
    },
    "cell.editor.value"(value, oldValue) {
      if (
        this.editorValue &&
        JSON.stringify(value) !== JSON.stringify(oldValue)
      ) {
        // this.editorValue.value = parseValue(this.cell.editor.mode, value);
        this.editorValue.value = value;
      }
    },
  },
  methods: {
    cleanTimerIfPresent() {
      if (this.timer) {
        clearInterval(this.timer);
      }
    },
    createTimer() {
      this.cleanTimerIfPresent();
      this.timer = setInterval(() => {
        this.flushText();
      }, this.flushPeriod);
    },
    flushText() {
      if (Date.now() - this.timestamp <= this.flushPeriod) {
        this.emitChange();
      }
    },
    emitChange() {
      const payload = {
        position: this.position,
        ...(this.editorValue || {}),
      };
      // console.log("我要结束编辑状态", payload);
      this.$emit("change-value", payload);
    },
    handleResize(a, b) {
      console.log(a, b);
    },
    handleDoubleClick(event) {
      if (this.isEditing) {
        return;
      }
      if (this.$listeners["double-click"]) {
        this.$emit("double-click", this.params, event);
      }
    },
    handleClick(event) {
      if (this.isEditing) {
        return;
      }
      if (this.$listeners["click"]) {
        this.$emit("click", this.params, event);
      }
    },
    handleMouseDown(event) {
      if (this.isEditing) {
        return;
      }
      if (this.$listeners["mouse-down"]) {
        this.$emit("mouse-down", this.params, event);
      }
    },
    handleMouseEnter(event) {
      if (this.isEditing) {
        return;
      }
      if (this.$listeners["mouse-enter"]) {
        this.$emit("mouse-enter", this.params, event);
      }
    },
    handleMouseUp(event) {
      if (this.isEditing) {
        return;
      }
      if (this.$listeners["mouse-up"]) {
        this.$emit("mouse-up", this.params, event);
      }
    },
    handleChangeValue(value) {
      // console.log("编辑器的内容改变了", value);
      this.editorValue = value;
      // if (!this.isEditing) {
      // 如果当前已经结束了编辑状态 要触发change事件
      // number input 很奇怪  是在结束编辑之后才触发change事件
      this.timestamp = Date.now();
      // }
    },
  },
};
</script>
<style>
.cell-text {
  word-break: keep-all;
  overflow: hidden;
  /*max-height: 24px;*/
  white-space: nowrap;
  text-overflow: ellipsis;
  max-width: 100%;
  display: inline-block;
  vertical-align: bottom;
}
</style>
